// Define the base URL as a constant
// Development fallback if environment variable is not set
const BASE_URL = process.env.REACT_APP_FLASK_API_ENDPOINT || "https://camplbellsoupsv1devui.azurewebsites.net/v1";

// Common fetch function to reduce code duplication

const fetchAPI = async (endpoint, method = "GET", body = null, signal = null) => {
  try {
    const url = `${BASE_URL}${endpoint}`;
    // console.log(`[API] Making ${method} request to:`, url);

    // Get Microsoft access token from localStorage
    const msftAccessToken = localStorage.getItem('accessToken');

    // Build headers object with Authorization if token exists
    const headers = {
      "Content-Type": "application/json",
      // "Accept": "application/json",
      // "Access-Control-Allow-Origin": "*",
      // "Access-Control-Allow-Methods": "GET, POST, PUT, DELETE, OPTIONS",
      // "Access-Control-Allow-Headers": "Content-Type, Authorization, Accept"
    };

    // Add Authorization header if Microsoft access token is available
    if (msftAccessToken) {
      headers["Authorization"] = `Bearer ${msftAccessToken}`;
    }

    const options = {
      method,
      headers,
      mode: 'cors',
      credentials: 'omit',
      signal
    };

    if (body && method !== "GET") {
      options.body = JSON.stringify(body);
    }

    const response = await fetch(url, options);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: `HTTP ${response.status}` }));
      // console.error(`[API] Request failed with status ${response.status}:`, errorData);
      throw new Error(errorData.message || `Request failed with status ${response.status}`);
    }

    const responseData = await response.json();
    // console.log(`[API] Request successful:`, responseData);
    return responseData;
  } catch (error) {
    // console.error(`[API] API call to ${endpoint} failed:`, error);

    // Handle CORS-specific errors
    if (error.name === 'TypeError' && error.message.includes('CORS')) {
      // console.error('[API] CORS error detected. Please ensure the backend server has proper CORS configuration.');
    }

    return {
      error: true,
      message: error.message || "Request failed"
    };
  }
};

// Helper function to get user information from Microsoft Graph API
export const getMicrosoftUserInfo = async (accessToken) => {
  try {
    const response = await fetch('https://graph.microsoft.com/v1.0/me', {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      console.error('Failed to fetch user info from Microsoft Graph');
      return null;
    }

    return await response.json();
  } catch (error) {
    console.error('Microsoft Graph API request failed:', error);
    return null;
  }
};

// Special fetch function for FormData (file uploads)
const fetchFormAPI = async (endpoint, formData) => {
  try {
    // Get Microsoft access token from localStorage
    const msftAccessToken = localStorage.getItem('accessToken');

    const options = {
      method: "POST",
      body: formData
    };

    // Add Authorization header if Microsoft access token is available
    if (msftAccessToken) {
      options.headers = {
        'Authorization': `Bearer ${msftAccessToken}`
      };
    }

    const response = await fetch(`${BASE_URL}${endpoint}`, options);

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `Request failed with status ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`API call to ${endpoint} failed:`, error);
    return {
      error: true,
      message: error.message || "Request failed"
    };
  }
};

// Bot response API
export const openAiResponse = async (promptin, followsession_id, enrichsession_id, plan_generation_id, signal) => {
  const body = {
    promptin,
    followsession_id,
    enrichsession_id,
    plan_generation_id,
  };

  return fetchAPI("/conceptResponse", "POST", body, signal);
};

// Agent response API
export const agentTastewiseResponse = async (promptin, followsession_id, enrichsession_id, plan_generation_id, question_id, datasource, signal) => {
  const body = {
    promptin,
    followsession_id,
    enrichsession_id,
    plan_generation_id,
    question_id,
    datasource
  };

  return fetchAPI("/newconceptResponse", "POST", body, signal);
};

// Agent for File Datasource response API
export const agentFileDatasourceResponse = async (promptin, followsession_id, enrichsession_id, plan_generation_id, question_id, datasource, file_names, signal) => {
  const body = {
    promptin,
    followsession_id,
    enrichsession_id,
    plan_generation_id,
    question_id,
    datasource,
    file_names,
  };

  return fetchAPI("/documents/query", "POST", body, signal);
};


// Cancel an ongoing agent request
export const cancelAgentRequest = async (question_id) => {
  return fetchAPI("/cancel", "POST", { question_id });
};

// supervisorAgentResponse API
export const supervisorAgentResponse = async (
  question,
  question_id,
  datasource,
  doc_filters,
  persona,
  user_id,
  thread_id,
  action,
  conversion_source = "",
  signal
) => {
  // Add user_id to filters by default
  const filters = { ...doc_filters, user_id };
  const body = {
    question,
    // followsession_id,
    // enrichsession_id,
    // plan_generation_id,
    question_id,
    datasource,
    filters,
    persona,
    user_id,
    thread_id,
    action,
    conversion_source
  };
  // console.log("supervisorAgentResponse body", body);
  return fetchAPI("/CombinedAgentsResponse", "POST", body, signal);
};


// Fetch file listing API
export const fetchFileListing = async () => {
  return fetchAPI("/documents/filenames");
};

export const fetchIngestedFileListing = async () => {
  // console.log("[API] Calling fetchIngestedFileListing...");
  const result = await fetchAPI("/file_progress_tracker");
  // console.log("[API] fetchIngestedFileListing response:", result);
  return result;
}

// New function to detect file changes by comparing current and previous file lists
export const hasFileChanges = (prevFiles, newFiles) => {
  if (!prevFiles || !newFiles) return false;

  // Check if number of files changed
  if (prevFiles.length !== newFiles.length) return true;

  // Create maps for faster lookup
  const prevFileMap = new Map(prevFiles.map(file => [file.file_name || file, file]));

  // Check if any new files exist that weren't in the previous list
  for (const file of newFiles) {
    const fileName = file.file_name || file;
    if (!prevFileMap.has(fileName)) {
      return true;
    }

    // If files have timestamps, check if timestamps differ
    if (file.timestamp && prevFileMap.get(fileName).timestamp !== file.timestamp) {
      return true;
    }
  }

  return false;
};

// Clear chat context API
export const clearChatContext = async () => {
  const response = await fetchAPI("/clearcontext", "POST", {});
  return response.status;
};

// Category API call
export const noResultCategoryApiCall = async (selectedCategory, selectedResultCategory, sourceUrl, userPrompt) => {
  const body = {
    category: selectedCategory,
    resultCategory: selectedResultCategory,
    url: sourceUrl,
    prompt: userPrompt,
  };

  return fetchAPI("/tastewise", "POST", body);
};

// Fetch file summary API
export const fetchFileSummary = async (selectedFile) => {
  return fetchAPI("/documents/summary", "POST", { job_run_id: selectedFile });
};

export const generatePrompt = async (payload) => {
  return fetchAPI("/generate_prompt", "POST", payload);
};



// Upload file API
export const uploadFile = async (formData) => {
  return fetchFormAPI("/file_upload", formData);
};

/**
 * Fetches the ingestion progress log for a specific file
 * @param {string} run_id - The name of the file to get progress for
 * @returns {Promise<Object>} The ingestion progress data
 */

export const getIngestionProgressLog = async (fileName, run_id) => {
  return fetchAPI("/file_progress_details", "POST", { run_id: run_id });
}

export const validateUser = async (email_id, department = "", firstName = "", lastName = "", designation = "") => {
  return fetchAPI("/user/validate", "POST", { 
    email: email_id, 
    department,
    first_name: firstName,
    last_name: lastName,
    designation
  });
};

/**
 * Create or ensure a SharePoint folder exists via MS Graph API.
 * @param {string} name - Folder name to create
 * @param {string} [library="Documents"] - Document library name
 * @param {string|null} [parentPath=null] - Parent folder path (relative), or null for root
 * @param {string} [conflictBehavior="rename"] - "fail" | "rename" | "replace"
 * @returns {Promise<Object>} Created/existing folder metadata
 */
export const createSharePointFolder = async (name, library = 'Documents',  conflictBehavior = 'rename') => {
  return fetchAPI("/sharepoint/folders", "POST", {
    name,
    library,
    conflict_behavior: conflictBehavior,

  });
};

/**
 * Manually triggers the SharePoint folder scan / sync job.
 * The job runs in the background; poll /sharepoint/scan/results for outcome.
 * @returns {Promise<Object>}
 */
export const triggerSharePointSync = async (email_id) => {
  return fetchAPI("/sharepoint/items?library=Documents&folder="+email_id+"&type=files");
};

/**
 * Fetch files uploaded to the user's SharePoint folder within the last N minutes.
 * @param {string} folder   - Drive-relative folder path (e.g. user_id)
 * @param {number} [within=1440] - Time window in minutes (1440 = 24 h)
 * @param {string} [library="Documents"]
 * @returns {Promise<Object>}
 */
export const getRecentSharePointFiles = async (folder, within = 1440, library = 'Documents') => {
  const params = new URLSearchParams({ library, within: String(within) });
  // console.log("getRecentSharePointFiles params:", { folder, within, library },`/sharepoint/files/recent?${params.toString()}`);
  if (folder) params.set('folder', folder);
  return fetchAPI(`/sharepoint/files/recent?${params.toString()}`);
};

/**
 * Verify which file names are present in the sharepoint_scanned_files table.
 * Uses GET /v1/sharepoint/scanned-files?file_name=<comma-separated names>
 * @param {string[]} fileNames - Array of file name strings
 * @returns {Promise<Object>} { status, total, data: [...] }
 */
export const verifyScannedFiles = async (fileNames) => {
  if (!fileNames || fileNames.length === 0) return { data: [] };
  const params = new URLSearchParams({ file_name: fileNames.join(',') });
  return fetchAPI(`/sharepoint/scanned-files?${params.toString()}`);
};

/**
 * POST /v1/sharepoint/scanned-files
 * Inserts a file record into the sharepoint_scanned_files table.
 * @param {Object} filePayload - Fields from the SharePoint file object
 * @returns {Promise<Object>}
 */
export const createScannedFileRecord = async (filePayload) => {
  return fetchAPI('/sharepoint/scanned-files', 'POST', filePayload);
};

export const getThreadList = async (user_id) => {
  return fetchAPI("/chat_group/list", "POST", { user_id: user_id });
};

export const addNewThread = async (user_id, thread_id, thread_name) => {
  return fetchAPI("/chat_group/create", "POST", { user_id: user_id, thread_id: thread_id, thread_name: thread_name });
};

export const deleteThread = async (user_id, thread_id) => {
  return fetchAPI("/chat_group/update_soft_delete", "PUT", { user_id: user_id.toString(), thread_id: thread_id, soft_delete: "true" });
};

export const updateThreadName = async (user_id, thread_id, thread_name) => {
  return fetchAPI("/chat_group/update_thread_name", "PUT", { user_id: user_id.toString(), thread_id: thread_id, thread_name: thread_name });
};

export const loadThreadChatList = async (user_id, thread_id) => {
  return fetchAPI("/chats/get_thread", "POST", { user_id: user_id, thread_id: thread_id });
};

export const submitChatFeedback = async (
  question_id,
  thread_id,
  user_id,
  feedback_type,
  feedback_reason = null,
  feedback_comment = null,
) => {
  return fetchAPI("/chats/feedback", "POST", {
    question_id,
    thread_id,
    user_id,
    feedback_type,
    feedback_reason,
    feedback_comment,
  });
};

export const deleteDocument = async (filename) => {
  return fetchAPI(`/documents/delete`, "DELETE", { filename });
};

/**
 * DELETE /v1/sharepoint/scanned-files/<item_id>
 * Fully delete a SharePoint scanned file (soft-deletes the SharePoint record
 * AND cleans up RAG chunks + vector-index entries server-side).
 * @param {string} item_id - The SharePoint DriveItem ID
 */
export const deleteSharePointFile = async (item_id) => {
  return fetchAPI(`/sharepoint/scanned-files/${encodeURIComponent(item_id)}`, "DELETE");
};

// ------------------------------------------------------------------ //
// CHIP Repository Hub — new endpoints (Tasks 1-6)
// ------------------------------------------------------------------ //

/**
 * GET /v1/sharepoint/scanned-files
 * Fetch paginated, filterable list of scanned files for the current user.
 * @param {{ limit?: number, offset?: number, library?: string, folder?: string, mime_type?: string, file_name?: string }} params
 */
export const getScannedFiles = async ({ limit = 20, offset = 0, library, folder, mime_type, file_name } = {}) => {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (library)   params.set('library',   library);
  if (folder)    params.set('folder',    folder);
  if (mime_type) params.set('mime_type', mime_type);
  if (file_name) params.set('file_name', file_name);
  return fetchAPI(`/sharepoint/scanned-files?${params.toString()}`);
};

/**
 * GET /v1/sharepoint/scanned-files/<item_id>/versions
 * Fetch the full version history for a single file.
 * @param {string} itemId
 */
export const getFileVersions = async (itemId) => {
  return fetchAPI(`/sharepoint/scanned-files/${encodeURIComponent(itemId)}/versions`);
};

/**
 * POST /v1/sharepoint/scanned-files/<item_id>/reprocess
 * Re-trigger document processing for an already-ingested SharePoint file.
 * @param {string} itemId
 */
export const reprocessFile = async (itemId) => {
  return fetchAPI(`/sharepoint/scanned-files/${encodeURIComponent(itemId)}/reprocess`, {
    method: "POST",
  });
};

/**
 * GET /v1/sharepoint/scanned-files/<item_id>/similar
 * Fetch semantically similar files across all users.
 * @param {string} itemId
 * @param {number} [topK=5]
 * @param {number} [minScore=0.85]
 */
export const getSimilarFiles = async (itemId, topK = 5, minScore = 0.85) => {
  const params = new URLSearchParams({ top_k: String(topK), min_score: String(minScore) });
  return fetchAPI(`/sharepoint/scanned-files/${encodeURIComponent(itemId)}/similar?${params.toString()}`);
};

/**
 * GET /v1/sharepoint/scanned-files/check-duplicate
 * Per-user duplicate check before sending a file to document processing.
 * Checks item_id → name → content_hash in priority order.
 * @param {{ itemId: string, name: string, contentHash?: string, downloadURL?: string }} params
 * @returns {Promise<{ match_type: 'item_id'|'name'|'content_hash'|'no_match', matched_file: object|null }>}
 */
export const checkFileDuplicate = async ({ itemId, name, contentHash, downloadURL } = {}) => {
  const params = new URLSearchParams();
  if (itemId)      params.set('item_id',      itemId);
  if (name)        params.set('name',         name);
  if (contentHash) params.set('content_hash', contentHash);
  if (downloadURL) params.set('download_url', downloadURL);
  return fetchAPI(`/sharepoint/scanned-files/check-duplicate?${params.toString()}`);
};

/**
 * GET /v1/sharepoint/scanned-files/check-global
 * Cross-user content_hash check — finds if any other user has already
 * processed a file with the same content.
 * @param {string} contentHash - SHA-256 hex digest (from checkFileDuplicate response)
 * @returns {Promise<{ match_found: bool, source_user_email: string|null, source_item_id: string|null, content_hash: string }>}
 */
export const checkGlobalDuplicate = async (contentHash) => {
  if (!contentHash) return { match_found: false };
  const params = new URLSearchParams({ content_hash: contentHash });
  return fetchAPI(`/sharepoint/scanned-files/check-global?${params.toString()}`);
};

/**
 * POST /v1/sharepoint/scanned-files/adopt
 * Adopt another user's already-processed document.
 * Grants shared RAG access with zero reprocessing cost.
 * @param {{ contentHash: string, sourceItemId: string, sourceUserId: string, name: string }} params
 * @returns {Promise<{ status: 'adopted', message: string }>}
 */
export const adoptFile = async ({ contentHash, sourceItemId, sourceUserId, name } = {}) => {
  return fetchAPI('/sharepoint/scanned-files/adopt', 'POST', {
    content_hash:   contentHash,
    source_item_id: sourceItemId,
    source_user_id: sourceUserId,
    name,
  });
};

/**
 * GET /v1/sharepoint/scanned-files/duplicates
 * Fetch groups of files sharing the same content hash (exact duplicates, cross-user).
 * @param {{ limit?: number, offset?: number }} params
 */
export const getDuplicateFiles = async ({ limit = 50, offset = 0 } = {}) => {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  return fetchAPI(`/sharepoint/scanned-files/duplicates?${params.toString()}`);
};

/**
 * GET /v1/sharepoint/scan/status
 * Fetch scheduler status: last run, next run, enabled flag.
 */
export const getScanStatus = async () => {
  return fetchAPI('/sharepoint/scan/status');
};

/**
 * GET /v1/sharepoint/scan/logs
 * Fetch paginated scan run history from sharepoint_scan_logs.
 * @param {{ limit?: number, offset?: number }} params
 */
export const getScanLogs = async ({ limit = 20, offset = 0 } = {}) => {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  return fetchAPI(`/sharepoint/scan/logs?${params.toString()}`);
};

/**
 * GET /v1/sharepoint/webhook/subscriptions
 * Fetch all webhook subscription records from the DB.
 */
export const getWebhookSubscriptions = async () => {
  return fetchAPI('/sharepoint/webhook/subscriptions');
};

/**
 * GET /v1/sharepoint/scanned-files/deleted
 * Fetch soft-deleted (reconciled) file records for the current user.
 * @param {{ limit?: number, offset?: number }} params
 */
export const getDeletedFiles = async ({ limit = 100, offset = 0 } = {}) => {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  return fetchAPI(`/sharepoint/scanned-files/deleted?${params.toString()}`);
};

// ------------------------------------------------------------------ //
// Admin endpoints (role_id === 1 only)
// ------------------------------------------------------------------ //

/**
 * GET /v1/admin/users
 * Paginated user list from user_details.
 * @param {{ search?: string, is_active?: '1'|'0', limit?: number, offset?: number }} params
 */
export const getAdminUsers = async ({ search = "", is_active = "", limit = 50, offset = 0 } = {}) => {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (search)    params.set("search",    search);
  if (is_active) params.set("is_active", is_active);
  return fetchAPI(`/admin/users?${params.toString()}`);
};

/**
 * GET /v1/admin/login-activity
 * Paginated login activity log.
 * @param {{ search?: string, status?: string, limit?: number, offset?: number }} params
 */
export const getAdminLoginActivity = async ({ search = "", status = "", limit = 50, offset = 0 } = {}) => {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (search) params.set("search", search);
  if (status) params.set("status", status);
  return fetchAPI(`/admin/login-activity?${params.toString()}`);
};

/**
 * GET /v1/admin/chat-activity
 * Per-user chat activity summary.
 * @param {{ search?: string, limit?: number, offset?: number }} params
 */
export const getAdminChatActivity = async ({ search = "", limit = 50, offset = 0 } = {}) => {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (search) params.set("search", search);
  return fetchAPI(`/admin/chat-activity?${params.toString()}`);
};

/**
 * GET /v1/admin/summary
 * Platform-wide summary counts (total users, active, inactive, logins today/week, groups, messages).
 */
export const getAdminSummary = async () => fetchAPI("/admin/summary");

/**
 * GET /v1/admin/user-metrics
 * Per-user metrics: login stats, chat stats, inactivity days.
 * @param {{ search?: string, is_active?: '1'|'0', days?: number, limit?: number, offset?: number }} params
 */
export const getAdminUserMetrics = async ({ search = "", is_active = "", days = 0, limit = 50, offset = 0 } = {}) => {
  const params = new URLSearchParams({ days: String(days), limit: String(limit), offset: String(offset) });
  if (search)    params.set("search",    search);
  if (is_active) params.set("is_active", is_active);
  return fetchAPI(`/admin/user-metrics?${params.toString()}`);
};

/**
 * GET /v1/admin/question-report
 * Per-question report: user, question text, sources, timestamp.
 * @param {{ search?: string, user_id?: string, date_from?: string, date_to?: string, limit?: number, offset?: number }} params
 */
export const getAdminQuestionReport = async ({ search = "", user_id = "", date_from = "", date_to = "", limit = 50, offset = 0 } = {}) => {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (search)    params.set("search",    search);
  if (user_id)   params.set("user_id",   user_id);
  if (date_from) params.set("date_from", date_from);
  if (date_to)   params.set("date_to",   date_to);
  return fetchAPI(`/admin/question-report?${params.toString()}`);
};

/**
 * GET /v1/admin/user-chat-history
 * Per-question chat rows for a specific user: group_name, question, datasource, timestamp.
 * @param {{ user_id: string, limit?: number, offset?: number }} params
 */
export const getAdminUserChatHistory = async ({ user_id, limit = 50, offset = 0, datasource } = {}) => {
  const params = new URLSearchParams({ user_id: String(user_id), limit: String(limit), offset: String(offset) });
  if (datasource) params.set("datasource", datasource);
  return fetchAPI(`/admin/user-chat-history?${params.toString()}`);
};

/**
 * GET /v1/admin/datasource-stats
 * Returns per-datasource question counts (split approach — comma-separated tokens aggregated in Python).
 */
export const getAdminDatasourceStats = async ({ user_id } = {}) => {
  const params = new URLSearchParams();
  if (user_id) params.append("user_id", user_id);
  const qs = params.toString();
  return fetchAPI(`/admin/datasource-stats${qs ? `?${qs}` : ""}`);
};

/**
 * GET /v1/admin/export/chat-history
 * All-users chat history export filtered by date range.
 * @param {{ start_date?: string, end_date?: string }} params YYYY-MM-DD
 */
export const getAdminExportChatHistory = async ({ start_date = "", end_date = "" } = {}) => {
  const params = new URLSearchParams();
  if (start_date) params.append("start_date", start_date);
  if (end_date)   params.append("end_date",   end_date);
  const qs = params.toString();
  return fetchAPI(`/admin/export/chat-history${qs ? `?${qs}` : ""}`);
};

/**
 * GET /v1/admin/export/login-log
 * All-users login log export filtered by date range.
 * @param {{ start_date?: string, end_date?: string }} params YYYY-MM-DD
 */
export const getAdminExportLoginLog = async ({ start_date = "", end_date = "" } = {}) => {
  const params = new URLSearchParams();
  if (start_date) params.append("start_date", start_date);
  if (end_date)   params.append("end_date",   end_date);
  const qs = params.toString();
  return fetchAPI(`/admin/export/login-log${qs ? `?${qs}` : ""}`);
};



// export const getIngestionProgressLog = async (fileName,run_id) => {
//   try {
//     const response = await fetch('/documents/getIngestionProgessStatus', {
//       method: 'POST',
//       headers: {
//         'Content-Type': 'application/json',
//       },
//       body: JSON.stringify({ fileName }),
//     });

//     if (!response.ok) {
//       throw new Error(`API request failed with status ${response.status}`);
//     }

//     const data = await response.json();
//     return { data };
//   } catch (error) {
//     console.error('Error fetching ingestion progress log:', error);
//     // For development/demo, return simulated data if API call fails
//     return {
//       data: simulateIngestionProgressData(fileName)
//     };
//   }
// };

// eslint-disable-next-line no-unused-vars
function simulateIngestionProgressData(fileName) {
  const runId = Date.now().toString();
  const baseTime = new Date();

  // List of all steps in the ingestion process
  const steps = [
    { step: "ingestion_pipeline", status: "started", message: "Starting document ingestion process" },
    { step: "check_file_exists", status: "started", message: "" },
    { step: "check_file_exists", status: "completed", message: "File exists check completed: True" },
    { step: "pdf_to_images", status: "started", message: "" },
    { step: "pdf_to_images", status: "completed", message: "PDF converted to 15 images" },
    { step: "vlm_processing", status: "started", message: "" },
    { step: "vlm_processing", status: "completed", message: "VLM processing complete: processed 15 pages" },
    { step: "embedding_generation", status: "started", message: "" },
    { step: "embedding_generation", status: "completed", message: "Generated embeddings for 15 chunks" },
    { step: "save_processed_data", status: "started", message: "" },
    { step: "save_processed_data", status: "completed", message: "Saved processed data with 15 rows" },
    { step: "embedding_upload_to_delta", status: "started", message: "" },
    { step: "embedding_upload_to_delta", status: "completed", message: "Successfully uploaded 15 records" },
    { step: "vector_index_sync", status: "started", message: "" },
    { step: "vector_index_sync", status: "completed", message: "Successfully synced vector index" },
    { step: "content_extraction", status: "started", message: "" },
    { step: "content_extraction", status: "completed", message: "Content extracted: ~20000 chars" },
    { step: "summary_generation", status: "started", message: "" },
    { step: "summary_generation", status: "completed", message: "Summary generated successfully: ~1500 chars" },
    { step: "summary_upload_to_delta", status: "started", message: "" },
    { step: "summary_upload_to_delta", status: "completed", message: "Summary successfully saved" },
    { step: "ingestion_pipeline", status: "completed", message: "Document ingestion pipeline completed successfully" }
  ];

  // Generate time-based sequence
  return steps.map((stepInfo, index) => {
    const timeOffset = index * 2000; // 2 seconds between steps
    const executionTime = stepInfo.status === "completed" ? Math.random() * 10 + 1 : 0;
    const startTime = new Date(baseTime.getTime() + timeOffset);
    const endTime = stepInfo.status === "completed"
      ? new Date(startTime.getTime() + executionTime * 1000)
      : startTime;

    return [
      runId,
      fileName,
      stepInfo.step,
      stepInfo.status,
      stepInfo.message,
      executionTime,
      startTime.toUTCString(),
      endTime.toUTCString(),
      index
    ];
  });
}

// Export BASE_URL for use in other files if needed
export const hostUrl = BASE_URL;

/**
 * Generate access token using email and persona combination
 * @param {string} userEmail - User's email address
 * @param {string} persona - User's persona (innovation, marketing, default, default2)
 * @returns {string} - Base64 encoded access token
 */
export const generateAccessToken = (userEmail, persona) => {
  // Simple token generation - in production, this should be done securely on the server
  const timestamp = Date.now();
  const tokenData = btoa(`${userEmail}:${persona}:${timestamp}`);
  return tokenData;
};

/**
 * Extract email from access token
 * @param {string} accessToken - The base64 encoded access token
 * @returns {string|null} - The email address or null if invalid
 */
export const getEmailFromAccessToken = (accessToken) => {
  try {
    if (!accessToken || typeof accessToken !== 'string') return null;

    // Check if the token is a valid base64 string
    const isValidBase64 = /^[A-Za-z0-9+/]*={0,2}$/.test(accessToken);
    if (!isValidBase64) {
      // Don't log error for missing/invalid tokens - these are expected scenarios
      return null;
    }

    // Decode the base64 token
    const decodedToken = atob(accessToken);
    // Split the token data (format: email:persona:timestamp)
    const tokenParts = decodedToken.split(':');
    return tokenParts[0] || null; // Return email (first part)
  } catch (error) {
    // Only log actual parsing errors, not expected validation failures
    console.error('Unexpected error parsing access token:', error);
    return null;
  }
};

/**
 * Extract persona from access token
 * @param {string} accessToken - The base64 encoded access token
 * @returns {string|null} - The persona or null if invalid
 */
export const getPersonaFromAccessToken = (accessToken) => {
  try {
    if (!accessToken || typeof accessToken !== 'string') return null;

    // Check if the token is a valid base64 string
    const isValidBase64 = /^[A-Za-z0-9+/]*={0,2}$/.test(accessToken);
    if (!isValidBase64) {
      // Don't log error for missing/invalid tokens - these are expected scenarios
      return null;
    }

    // Decode the base64 token
    const decodedToken = atob(accessToken);
    // Split the token data (format: email:persona:timestamp)
    const tokenParts = decodedToken.split(':');
    return tokenParts[1] || null; // Return persona (second part)
  } catch (error) {
    console.error('Unexpected error parsing access token:', error);
    return null;
  }
};

/**
 * Get current user email from stored access token
 * @returns {string|null} - The current user's email or null
 */
export const getCurrentUserEmail = () => {
  const accessToken = localStorage.getItem('accessToken');
  return getEmailFromAccessToken(accessToken);
};

/**
 * Validate if access token is still valid (basic check)
 * @param {string} accessToken - The access token to validate
 * @returns {boolean} - True if token appears valid
 */
export const isValidAccessToken = (accessToken) => {
  try {
    if (!accessToken || typeof accessToken !== 'string') return false;

    // Check if the token is a valid base64 string
    const isValidBase64 = /^[A-Za-z0-9+/]*={0,2}$/.test(accessToken);
    if (!isValidBase64) return false;

    const decodedToken = atob(accessToken);
    const tokenParts = decodedToken.split(':');

    // Check if token has all required parts
    return tokenParts.length === 3 && tokenParts[0] && tokenParts[1] && tokenParts[2];
  } catch (error) {
    return false;
  }
};

/**
 * Extract first and last name from email address
 * @param {string} email - The email address
 * @returns {object} - Object containing firstName and lastName
 */
export const getNameFromEmail = (email) => {
  if (!email) return { firstName: '', lastName: '' };

  try {
    // Get the part before @ symbol
    const localPart = email.split('@')[0];

    // Split by common separators (dot, underscore, dash)
    const nameParts = localPart.split(/[._-]+/);

    if (nameParts.length >= 2) {
      return {
        firstName: capitalizeFirstLetter(nameParts[0]),
        lastName: capitalizeFirstLetter(nameParts[1])
      };
    } else if (nameParts.length === 1) {
      return {
        firstName: capitalizeFirstLetter(nameParts[0]),
        lastName: ''
      };
    }

    return { firstName: '', lastName: '' };
  } catch (error) {
    console.error('Error parsing name from email:', error);
    return { firstName: '', lastName: '' };
  }
};

/**
 * Capitalize first letter of a string
 * @param {string} str - String to capitalize
 * @returns {string} - Capitalized string
 */
const capitalizeFirstLetter = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Get full name from email address
 * @param {string} email - The email address
 * @returns {string} - Full name (firstName lastName)
 */
export const getFullNameFromEmail = (email) => {
  const { firstName, lastName } = getNameFromEmail(email);
  return `${firstName} ${lastName}`.trim();
};

export const persona_configurations = {
  innovation: {
    displayName: "Innovation Manager",
    description: "Focuses on driving innovation and new product development.",
    accessLevel: "advanced",
    availableDataSources: ["tastewise", "documents", "mintel", "SPINS"]
  },
  marketing: {
    displayName: "Marketing Specialist",
    description: "Specializes in market research and promotional strategies.",
    accessLevel: "intermediate",
    availableDataSources: ["tastewise", "documents", "mintel"]
  },
  default: {
    displayName: "General User",
    description: "Access to standard features and functionalities.",
    accessLevel: "basic",
    availableDataSources: ["tastewise", "documents", "mintel"]
  },
  default2: {
    displayName: "Standard User",
    description: "Access to all general features including advanced datasources.",
    accessLevel: "basic",
    availableDataSources: ["tastewise", "documents", "mintel", "circana", "SPINS", "SproutSocial", "Yogi"]
  },
  microsoft: {
    displayName: "Microsoft User",
    description: "Authenticated via Microsoft OAuth with standard access.",
    accessLevel: "basic",
    availableDataSources: ["tastewise", "documents", "mintel", "circana", "SPINS", "SproutSocial", "Yogi"]
  },
  sproutsocial: {
    displayName: "SproutSocial User",
    description: "Access to SproutSocial datasource only.",
    accessLevel: "basic",
    availableDataSources: ["SproutSocial"]
  }
};

export const RESEARCH_MODE = {
  DESIGN_INTELLIGENCE: "DESIGN_INTELLIGENCE",
  CONSUMER_SENTIMENT: "CONSUMER_SENTIMENT",
  ALL_SOURCE: "ALL_SOURCE",
};

export const CONVERSION_SOURCE = {
  CIRCANA: "CIRCANA",
  SPINS: "SPINS",
};

export const DEFAULT_DESIGN_INTELLIGENCE_SOURCES = ["tastewise", "mintel", "documents"];
export const CONSUMER_SENTIMENT_SOURCES = ["sproutsocial", "yogi", "documents"];

export const getResearchModePreference = async () => {
  return fetchAPI("/user/preferences/research-mode");
};

export const saveResearchModePreference = async (payload) => {
  return fetchAPI("/user/preferences/research-mode", "PUT", payload);
};

